import { useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Chart, ChartArea, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartSeries,
  ChartSeriesItem, ChartTooltip, ChartValueAxis, ChartValueAxisItem,
} from '@progress/kendo-react-charts';

const C = {
  agent: '#f59e0b',
  human: '#3b82f6',
  other: '#64748b',
  share: '#22c55e',
  note: '#e0632b',
  axis: '#94a3b8',
  grid: 'rgba(148,163,184,0.18)',
};

export type BillingComplianceDailyRow = {
  Day: string; AgentSrtp: number; HumanSrtp: number; OtherAutomationSrtp: number;
};

export type BillingComplianceCost = {
  RangeUsd: number; TrackedSince: string | null; Complete: boolean;
};

export type BillingComplianceSummary = {
  AgentSrtp: number;
  HumanSrtp: number;
  OtherAutomationSrtp: number;
  TotalSrtp: number;
  AgentSharePct: number;
  DailyTrend: BillingComplianceDailyRow[];
  Cost: BillingComplianceCost;
};

function usd(value: number | null | undefined, digits = 2): string {
  if (value == null) return '—';
  return `$${value.toFixed(digits)}`;
}

function latencySeconds(ms: number | null | undefined): string {
  if (ms == null) return '—';
  return `${(ms / 1000).toFixed(1)}s`;
}

// ---- Collapsed card ----
export function BillingComplianceCard({ summary, avgLatencyMs, selected, onClick }: {
  summary: BillingComplianceSummary; avgLatencyMs: number | null | undefined; selected: boolean; onClick: () => void;
}) {
  return (
    <div
      className={`card h-100 shadow-sm${selected ? ' border-primary' : ''}`}
      style={{ cursor: 'pointer', ...(selected ? { borderLeft: `4px solid ${C.agent}` } : {}) }}
      onClick={onClick}
    >
      <div className="card-body">
        <h6 className="card-title font-weight-bold">Billing Compliance</h6>
        <div className="mb-2">
          <span className="font-weight-bold" style={{ color: C.agent, fontSize: '2rem' }}>{summary.AgentSharePct}%</span>
          <span className="text-muted ml-1" style={{ fontSize: '0.8rem' }}>of SRTPs by AI</span>
        </div>
        <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
          <strong>{summary.AgentSrtp.toLocaleString()}</strong> AI &nbsp;
          <strong>{summary.HumanSrtp.toLocaleString()}</strong> human &nbsp;
          <strong>{summary.OtherAutomationSrtp.toLocaleString()}</strong> other automation
        </div>
        <div className="d-flex align-items-center">
          {summary.Cost?.TrackedSince ? (
            <span
              className="badge badge-secondary"
              title={summary.Cost.Complete
                ? 'Estimated at list prices'
                : `Estimated at list prices. Image review cost is tracked since ${summary.Cost.TrackedSince}, so earlier dates are understated.`}
            >
              Cost: {usd(summary.Cost.RangeUsd)}{summary.Cost.Complete ? '' : '+'}
            </span>
          ) : (
            <span className="badge badge-secondary" title="Cost shows once image review spend is being recorded">Cost: N/A</span>
          )}
          <span className="badge badge-light border ml-1">{latencySeconds(avgLatencyMs)}/turn</span>
        </div>
      </div>
    </div>
  );
}

// ---- Detail panel ----
export function BillingComplianceDetail({ summary, onClose }: {
  summary: BillingComplianceSummary; onClose: () => void;
}) {
  const trend = useMemo(() => {
    const rows = summary.DailyTrend;
    return {
      days: rows.map(r => dayjs(r.Day).format('MMM D')),
      agent: rows.map(r => r.AgentSrtp),
      human: rows.map(r => r.HumanSrtp),
      other: rows.map(r => r.OtherAutomationSrtp),
      share: rows.map(r => {
        const total = r.AgentSrtp + r.HumanSrtp + r.OtherAutomationSrtp;
        return total > 0 ? Math.round((r.AgentSrtp / total) * 1000) / 10 : null;
      }),
    };
  }, [summary.DailyTrend]);

  return (
    <div className="card mt-3 shadow-sm">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <span className="rounded-circle mr-2" style={{ display: 'inline-block', width: 10, height: 10, background: C.agent }} />
          <strong>Billing Compliance</strong>
          <span className="text-muted ml-2" style={{ fontSize: '0.85rem' }}>— selected date range</span>
        </div>
        <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
      </div>
      <div className="card-body">
        {summary.TotalSrtp === 0 ? (
          <p className="text-muted mb-0">No invoices set ready to post in this period.</p>
        ) : (
          <>
            <div className="mb-3">
              <span className="badge mr-2 p-2" style={{ background: '#fef3c7', color: '#b45309' }}>
                {summary.AgentSharePct}% set ready to post by AI
              </span>
              <span className="badge mr-2 p-2" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                {summary.HumanSrtp.toLocaleString()} by humans
              </span>
              <span className="badge p-2" style={{ background: '#e2e8f0', color: '#334155' }}>
                {summary.TotalSrtp.toLocaleString()} total SRTPs
              </span>
            </div>
            <Chart style={{ height: 300 }}>
              <ChartArea background="transparent" />
              <ChartTooltip shared={true} />
              <ChartLegend position="bottom" labels={{ color: C.axis, font: '11px sans-serif' }} />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem
                  categories={trend.days}
                  labels={{ color: C.axis, font: '10px sans-serif', rotation: trend.days.length > 10 ? 35 : 0 }}
                  line={{ color: C.grid }}
                  majorGridLines={{ visible: false }}
                  axisCrossingValue={[0, trend.days.length]}
                />
              </ChartCategoryAxis>
              <ChartValueAxis>
                <ChartValueAxisItem
                  name="count"
                  labels={{ color: C.axis, font: '10px sans-serif' }}
                  line={{ visible: false }}
                  majorGridLines={{ color: C.grid }}
                />
                <ChartValueAxisItem
                  name="share"
                  min={0}
                  max={100}
                  labels={{ color: C.axis, font: '10px sans-serif', format: '{0}%' }}
                  line={{ visible: false }}
                  majorGridLines={{ visible: false }}
                />
              </ChartValueAxis>
              <ChartSeries>
                <ChartSeriesItem type="column" stack={{ group: 'srtp' }} name="AI" axis="count" color={C.agent} data={trend.agent} />
                <ChartSeriesItem type="column" stack={{ group: 'srtp' }} name="Human" axis="count" color={C.human} data={trend.human} />
                <ChartSeriesItem type="column" stack={{ group: 'srtp' }} name="Other automation" axis="count" color={C.other} data={trend.other} />
                <ChartSeriesItem type="line" name="AI share %" axis="share" color={C.share} markers={{ visible: true, size: 5 }} data={trend.share} tooltip={{ format: '{0}%' }} />
              </ChartSeries>
            </Chart>
            <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
              <strong>How to read this chart</strong>
              <ul className="mb-0 pl-3">
                <li style={{ color: C.note, fontWeight: 600 }}>
                  Certain customer accounts are excluded from AI scope for SRTP, so their invoices are always set
                  ready to post by the billing team. They count toward the human total, which lowers the AI share.
                </li>
                <li>
                  Each column is one day (UTC). Its height is the number of invoices set ready to post (SRTP)
                  that day, split by who did it: <strong>AI</strong> is the Billing Compliance agent,{' '}
                  <strong>Human</strong> is billing staff, and <strong>Other automation</strong> is the ePay
                  document classifier. Read these against the left axis.
                </li>
                <li>
                  The green line is that day&apos;s AI share: AI SRTPs divided by all SRTPs that day, read against
                  the right axis (0 to 100%). Days with no SRTPs have no point.
                </li>
                <li>
                  The headline percentage covers the whole date range (total AI SRTPs divided by total SRTPs), so
                  busy days weigh more. It is not an average of the daily line.
                </li>
                <li>
                  A high share on a low column (often weekends, when staff are off and the agent keeps working)
                  rests on only a few invoices. Check the column height before reading much into a spike.
                </li>
                <li>
                  An invoice set ready to post more than once counts once, on the latest date, for whoever did
                  it last. Credit memos and invoices posted without ever being set ready to post are excluded.
                </li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
